import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import AdminLayout from '../components/AdminLayout'
import { AdminTable, Column } from '../components/AdminTable'
import { CalendarDays } from 'lucide-react'
import adminService from '../utils/adminService'
import { ApiError } from '../utils/api'
import { formatAmount } from '../utils/amountFormatter'

type FilingRow = {
  key: string
  type: string
  period: string
  filingDate: string
  submittedBy: string
  dueDate: string
  taxPayable: string
  docs: string
  status: string
  statusTone: 'success' | 'warning' | 'danger'
}

type TransactionRow = {
  key: string
  id: string
  date: string
  service: string
  amount: string
  refId: string
  status: 'Successful' | 'Pending' | 'Failed'
}

type CompanyRow = {
  key: string
  sn: number
  name: string
  createdAt: string
  owner: string
  status: string
  lastFiling: string
}

const formatDate = (value?: string) =>
  value
    ? new Date(value).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—'

const titleCase = (value?: string) =>
  value
    ? value
        .toString()
        .toLowerCase()
        .replace(/[_-]/g, ' ')
        .split(' ')
        .filter(Boolean)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    : '—'

export default function AdminUserDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<'companies' | 'filings' | 'transactions'>('filings')
  const [user, setUser] = useState<any | null>(null)
  const [userLoading, setUserLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [dateRange] = useState('15 Jun - 21 Jun 2025')

  const [companies, setCompanies] = useState<CompanyRow[]>([])
  const [companyPage, setCompanyPage] = useState(1)
  const [companyTotal, setCompanyTotal] = useState(0)
  const [companiesLoading, setCompaniesLoading] = useState(false)

  const [filings, setFilings] = useState<FilingRow[]>([])
  const [filingPage, setFilingPage] = useState(1)
  const [filingTotal, setFilingTotal] = useState(0)
  const [filingsLoading, setFilingsLoading] = useState(true)

  const [transactions, setTransactions] = useState<TransactionRow[]>([])
  const [transactionPage, setTransactionPage] = useState(1)
  const [transactionTotal, setTransactionTotal] = useState(0)
  const [transactionsLoading, setTransactionsLoading] = useState(false)

  const itemsPerPage = 10

  useEffect(() => {
    const loadUser = async () => {
      if (!id) return
      setError(null)
      setUserLoading(true)
      try {
        const { response } = await adminService.getUser(id)
        const data: any = (response as any)?.data || response
        setUser(data?.user || data)
      } catch (err) {
        console.error(err)
        setError(err instanceof ApiError ? err.message : 'Unable to load user details.')
      } finally {
        setUserLoading(false)
      }
    }

    void loadUser()
  }, [id])

  useEffect(() => {
    if (!id || activeTab !== 'companies') return
    const load = async () => {
      setCompaniesLoading(true)
      try {
        const { response } = await adminService.listCompanies({
          page: companyPage,
          limit: itemsPerPage,
          userId: id,
        })
        const data: any = (response as any)?.data || response
        const items = data?.items || data?.data?.items || data?.data || []
        setCompanyTotal(data?.total || data?.companies?.total || items.length || 0)
        setCompanies(
          items.map((item: any, idx: number) => ({
            key: item.id || item._id || `${idx}`,
            sn: (companyPage - 1) * itemsPerPage + idx + 1,
            name: item.companyName || item.name || '—',
            createdAt: formatDate(item.createdAt),
            owner:
              item.companyOwner?.name ||
              item.companyOwner?.email ||
              [item.companyOwner?.firstName, item.companyOwner?.lastName].filter(Boolean).join(' ') ||
              '—',
            status: titleCase(item.companyStatus || item.status || ''),
            lastFiling: formatDate(item.lastFiling?.createdAt || item.lastFilingDate),
          })),
        )
      } catch (err) {
        console.error(err)
        setError(err instanceof ApiError ? err.message : 'Unable to load companies.')
      } finally {
        setCompaniesLoading(false)
      }
    }
    void load()
  }, [id, activeTab, companyPage])

  useEffect(() => {
    if (!id || activeTab !== 'filings') return
    const load = async () => {
      setFilingsLoading(true)
      try {
        const { response } = await adminService.listFilings({
          page: filingPage,
          limit: itemsPerPage,
          userId: id,
        })
        const data: any = (response as any)?.data || response
        const items = data?.items || data?.data?.items || data?.data || []
        const summary = data?.summary || {}
        setFilingTotal(summary.total ?? data?.total ?? items.length ?? 0)
        setFilings(
          items.map((item: any, idx: number) => {
            const statusRaw = (item.status || item.filingStatus || '').toString().toLowerCase()
            const statusTone: FilingRow['statusTone'] =
              ['submitted', 'completed', 'accepted', 'successful'].includes(statusRaw)
                ? 'success'
                : statusRaw.includes('draft') || statusRaw.includes('pending') || statusRaw.includes('progress')
                ? 'warning'
                : 'danger'
            return {
              key: item.id || item._id || `${idx}`,
              type: item.type || item.filingType || '—',
              period:
                item.period ||
                item.taxYear ||
                (item.accountingPeriodStart && item.accountingPeriodEnd
                  ? `${formatDate(item.accountingPeriodStart)} - ${formatDate(item.accountingPeriodEnd)}`
                  : '—'),
              filingDate: formatDate(item.filingDate),
              submittedBy:
                item.submittedBy?.name ||
                [item.submittedBy?.firstName, item.submittedBy?.lastName].filter(Boolean).join(' ') ||
                item.submittedBy?.email ||
                '—',
              dueDate: formatDate(item.dueDate),
              taxPayable: item.taxPayable ? `£${Number(item.taxPayable).toLocaleString()}` : '—',
              docs: item.docs ? String(item.docs) : (item.documents?.length ? String(item.documents.length) : '—'),
              status: titleCase(statusRaw) || '—',
              statusTone,
            }
          }),
        )
      } catch (err) {
        console.error(err)
        setError(err instanceof ApiError ? err.message : 'Unable to load filings.')
      } finally {
        setFilingsLoading(false)
      }
    }
    void load()
  }, [id, activeTab, filingPage])

  useEffect(() => {
    if (!id || activeTab !== 'transactions') return
    const load = async () => {
      setTransactionsLoading(true)
      try {
        const { response } = await adminService.listTransactions({
          page: transactionPage,
          limit: itemsPerPage,
          userId: id,
        })
        const data: any = (response as any)?.data || response
        const items = data?.data?.data || data?.data || data?.items || []
        const meta = data?.data?.meta || data?.meta || {}
        setTransactionTotal(meta.total || data?.total || items.length || 0)
        setTransactions(
          items.map((item: any, idx: number) => {
            const amountInPounds = item.amount ? item.amount / 100 : 0
            const currencySymbol = item.currency === 'gbp' ? '£' : '£'
            let status: TransactionRow['status'] = 'Failed'
            if (item.status === 'paid' || item.status === 'completed') status = 'Successful'
            else if (item.status === 'pending' || item.status === 'processing') status = 'Pending'
            return {
              key: item._id || item.id || `${idx}`,
              id: item._id || item.id || '—',
              date: formatDate(item.createdAt),
              service: item.planSlug || item.type || '—',
              amount: `${currencySymbol}${formatAmount(amountInPounds)}`,
              refId: item.paymentIntentId || item.sessionId || item._id || '—',
              status,
            }
          }),
        )
      } catch (err) {
        console.error(err)
        setError(err instanceof ApiError ? err.message : 'Unable to load transactions.')
      } finally {
        setTransactionsLoading(false)
      }
    }
    void load()
  }, [id, activeTab, transactionPage])

  const filingColumns: Column<FilingRow>[] = [
    { key: 'type', label: 'Type' },
    { key: 'period', label: 'Period' },
    { key: 'filingDate', label: 'Filing Date' },
    { key: 'submittedBy', label: 'Submitted By' },
    { key: 'dueDate', label: 'Due Date' },
    { key: 'taxPayable', label: 'Tax Payable' },
    { key: 'docs', label: 'Docs', render: row => <span className='text-gray-500'>{row.docs}</span> },
    {
      key: 'status',
      label: 'Status',
      render: row => {
        const style =
          row.statusTone === 'success'
            ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
            : row.statusTone === 'warning'
            ? 'bg-amber-50 text-amber-700 border-amber-100'
            : 'bg-red-50 text-red-700 border-red-100'
        return (
          <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold border ${style}`}>
            {row.status}
          </span>
        )
      },
    },
    {
      key: 'actions',
      label: 'Actions',
      render: row => (
        <button
          onClick={() => navigate(`/admin/filings/${row.key}`)}
          className='inline-flex items-center gap-1 rounded-full border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-800 hover:bg-gray-50'
        >
          View
        </button>
      ),
    },
  ]

  const transactionColumns: Column<TransactionRow>[] = [
    { key: 'id', label: 'ID' },
    { key: 'date', label: 'Date' },
    { key: 'service', label: 'Service' },
    { key: 'amount', label: 'Amount' },
    { key: 'refId', label: 'Ref ID' },
    {
      key: 'status',
      label: 'Status',
      render: row => {
        const style =
          row.status === 'Successful'
            ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
            : row.status === 'Pending'
            ? 'bg-amber-50 text-amber-700 border-amber-100'
            : 'bg-red-50 text-red-700 border-red-100'
        return (
          <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold border ${style}`}>
            {row.status}
          </span>
        )
      },
    },
    {
      key: 'actions',
      label: 'Actions',
      render: () => (
        <button className='inline-flex items-center gap-1 rounded-full border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-800 hover:bg-gray-50'>
          Receipt
        </button>
      ),
    },
  ]

  const companyColumns: Column<CompanyRow>[] = [
    { key: 'sn', label: 'S/N' },
    { key: 'name', label: 'Company Name' },
    { key: 'createdAt', label: 'Create At' },
    { key: 'owner', label: 'Company Owner' },
    {
      key: 'status',
      label: 'Status',
      render: row => (
        <span className='inline-flex items-center rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700 border border-emerald-100'>
          {row.status}
        </span>
      ),
    },
    { key: 'lastFiling', label: 'Last Filing' },
    {
      key: 'actions',
      label: 'Actions',
      render: row => (
        <button
          onClick={() => navigate(`/admin/companies/${row.key}`)}
          className='inline-flex items-center gap-1 rounded-full border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-800 hover:bg-gray-50'
        >
          View
        </button>
      ),
    },
  ]

  const handleSuspend = async () => {
    if (!id || !user) return
    try {
      await adminService.updateUserStatus(id, { isActive: !user.isActive })
      setUser({ ...user, isActive: !user.isActive })
    } catch (err) {
      console.error(err)
      setError(err instanceof ApiError ? err.message : 'Unable to update user status.')
    }
  }

  return (
    <AdminLayout>
      <div className='px-4 py-4 sm:px-6 sm:py-6 lg:px-10 space-y-5 sm:space-y-6'>
        <h2 className='text-lg sm:text-xl font-bold text-gray-900'>User Management</h2>

        <div className='grid grid-cols-1 gap-4 lg:grid-cols-[320px,1fr]'>
          <div className='rounded-2xl bg-white border border-gray-100 shadow-sm p-4 sm:p-5 flex flex-col justify-between'>
            <div>
              <div className='flex items-center justify-between mb-4 gap-2'>
                <button
                  onClick={() => window.history.back()}
                  className='h-8 w-8 rounded-full border border-gray-200 flex items-center justify-center text-gray-700 hover:text-gray-900 flex-shrink-0'
                >
                  ←
                </button>
                <div className='inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs sm:text-sm text-gray-700 shadow-sm'>
                  <CalendarDays className='h-4 w-4 text-gray-500 flex-shrink-0' />
                  <span className='whitespace-nowrap'>{dateRange}</span>
                </div>
              </div>
              {userLoading ? (
                <div className='space-y-3 animate-pulse'>
                  <div className='h-5 w-2/3 rounded bg-gray-200' />
                  <div className='h-3 w-full rounded bg-gray-200' />
                  <div className='h-3 w-5/6 rounded bg-gray-200' />
                  <div className='h-3 w-4/6 rounded bg-gray-200' />
                  <div className='h-3 w-3/4 rounded bg-gray-200' />
                  <div className='h-3 w-2/3 rounded bg-gray-200' />
                  <div className='h-3 w-5/6 rounded bg-gray-200' />
                </div>
              ) : (
              <div className='space-y-2 text-sm text-gray-700 break-words'>
                <p className='text-lg font-semibold text-gray-900'>
                  {[user?.firstName, user?.lastName].filter(Boolean).join(' ') || 'User'}
                </p>
                <p><span className='font-semibold'>Email:</span> {user?.email || '—'}</p>
                <p><span className='font-semibold'>Phone Number:</span> {user?.phoneNumber || user?.phone || '—'}</p>
                <p>
                  <span className='font-semibold'>Status:</span>{' '}
                  <span className='inline-flex items-center rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700 border border-emerald-100'>
                    {user?.isActive ? 'Active' : 'Inactive'}
                  </span>
                </p>
                <p><span className='font-semibold'>No Of Filing:</span> {user?.noOfFilings ?? user?.filingsCount ?? filingTotal}</p>
                <p><span className='font-semibold'>Registered Companies:</span> {user?.registeredCompanies ?? user?.companiesCount ?? companyTotal}</p>
                <p><span className='font-semibold'>Date Registered:</span> {formatDate(user?.createdAt)}</p>
                <p><span className='font-semibold'>Last Login:</span> {formatDate(user?.lastLogin)}</p>
              </div>
              )}
            </div>
            <button
              onClick={handleSuspend}
              className='mt-6 w-full rounded-xl border border-gray-300 bg-gray-50 px-4 py-3 text-sm font-semibold text-gray-800 hover:bg-gray-100'
            >
              {user?.isActive ? 'Suspend User' : 'Reactivate User'}
            </button>
          </div>

          <div className='rounded-2xl bg-white border border-gray-100 shadow-sm p-4 sm:p-5 space-y-4 min-w-0'>
            {error && (
              <div className='rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700'>
                {error}
              </div>
            )}
            <div className='flex flex-wrap items-center gap-2'>
              <button
                onClick={() => setActiveTab('companies')}
                className={`rounded-lg px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold ${
                  activeTab === 'companies' ? 'bg-[#0b1f3a] text-white' : 'bg-white border border-gray-200 text-gray-800'
                }`}
              >
                Registered Companies
              </button>
              <button
                onClick={() => setActiveTab('filings')}
                className={`rounded-lg px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold ${
                  activeTab === 'filings' ? 'bg-[#0b1f3a] text-white' : 'bg-white border border-gray-200 text-gray-800'
                }`}
              >
                Filing History
              </button>
              <button
                onClick={() => setActiveTab('transactions')}
                className={`rounded-lg px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold ${
                  activeTab === 'transactions' ? 'bg-[#0b1f3a] text-white' : 'bg-white border border-gray-200 text-gray-800'
                }`}
              >
                Transaction History
              </button>
            </div>

            {activeTab === 'companies' && (
              <AdminTable<CompanyRow>
                columns={companyColumns}
                data={companies}
                loading={companiesLoading}
                emptyMessage='No companies registered by this user.'
                searchPlaceholder='Search Company'
                actions={null}
                currentPage={companyPage}
                totalItems={companyTotal}
                itemsPerPage={itemsPerPage}
                onPageChange={setCompanyPage}
              />
            )}
            {activeTab === 'filings' && (
              <AdminTable<FilingRow>
                columns={filingColumns}
                data={filings}
                loading={filingsLoading}
                emptyMessage='No filings for this user.'
                searchPlaceholder='Search Filings'
                actions={null}
                currentPage={filingPage}
                totalItems={filingTotal}
                itemsPerPage={itemsPerPage}
                onPageChange={setFilingPage}
              />
            )}
            {activeTab === 'transactions' && (
              <AdminTable<TransactionRow>
                columns={transactionColumns}
                data={transactions}
                loading={transactionsLoading}
                emptyMessage='No transactions for this user.'
                searchPlaceholder='Search Transactions'
                actions={null}
                currentPage={transactionPage}
                totalItems={transactionTotal}
                itemsPerPage={itemsPerPage}
                onPageChange={setTransactionPage}
              />
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  )
}
